import { FC, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Alert, Input, Typography } from 'antd'
import { CenteredSpin, Modal } from '../../../ui'
import { encode, btoa } from 'js-base64'
import { ButtonsBlock } from '../../../Edit/components'
import { openAccountService } from '../../../../services/accounts'
import { getAccounts } from '../../../../store/accounts'
import {createSignedPdf, errorHandler, isExpired} from '../../../helpers'
import { useAppSelector } from '../../../../store/hooks'
import SignUtils from '../../../../utils/sign/SignUtils'
import { hexToBinary } from '../../../helpers'
import { v4 as uuidv4 } from 'uuid'
import { useGetTariffRate, useGetUserTariffRate } from '../../../hooks'
import style from './style.m.less'
import { EUserRole, EUserType } from '../../../../constants/approval'
import { useCurrentUser } from '../../../hooks/useCurrentUser'
import {EServiceIDs} from "../../../../constants/documents";

interface IOpenAccountModal {
    isModalVisible: boolean
    userId?: string
    setIsModalVisible: (value: boolean) => void
    userRole?: number
}

// Типы шаблонов для получения pdf распоряжения об открытии ЛС
type TemplateName =
    | 'open_account_number_natural_person'
    | 'open_account_number_legal_entity'
    | 'open_account_number_legal_entity_DG'
    | 'open_account_number_individual_entrepreneur'

// Интерфейс запроса для получения pdf распоряжения об открытии ЛС
interface IRequestGetPDF {
    template_name: TemplateName
    data: {
        user_name: string
        company_name?: string
        inn_kpp_ogrn?: string
        company_position?: string
        attorney_number?: string
        snils?: string
        ogrnip?: string
    }
}

export const accNameMaxLength = 20
export const accNameMaxLengthErrMsg = `Наименование лицевого счета больше ${accNameMaxLength} символов`

const OpenAccountModal: FC<IOpenAccountModal> = ({
    isModalVisible,
    userId,
    setIsModalVisible,
    userRole,
}) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const { isUserAdmin } = useCurrentUser()

    const [name, setName] = useState<string>('')
    const [responseError, setResponseError] = useState<string>('')

    const userInfo = useAppSelector((store) => store.user)
    const { data } = userInfo
    const isClientGOOwnerTSO =
        data?.user_role == EUserRole.Client ||
        data?.user_role == EUserRole.GOOwner ||
        data?.user_role == EUserRole.TSO
    const isSignExpired = isClientGOOwnerTSO && isExpired(data?.certificate_data.token_expiration)
    // У пользователя должна быть МЧД
    const needAttorney =
        data?.user_type === EUserType.UrLico && isClientGOOwnerTSO && !data?.without_attorney
    // У пользователя должна быть МЧД, но её нет
    const isEmptyAttorney = needAttorney && !data?.machine_readable_attorney
    // У пользователя есть МЧД и у неё истёк срок
    const isAttorneyExpired =
        needAttorney &&
        !!data?.machine_readable_attorney &&
        isExpired(data?.machine_readable_attorney.validity_period, true)

    const onChangeName = (e: any) => {
        if (name.length > accNameMaxLength) {
            setResponseError(accNameMaxLengthErrMsg)
        } else {
            setResponseError('')
        }

        setName(e.target.value)
    }

    const handleCancel = () => {
        setResponseError('')
        setIsModalVisible(false)
    }

    const { isLoading, balance, rate } = isUserAdmin
        ? useGetUserTariffRate({
              dependency: isModalVisible,
              id: 10,
              showError: !userId,
              userId: userId,
              userRole: userRole,
          })
        : useGetTariffRate({
              dependency: isModalVisible,
              id: 10,
              showError: !userId,
          })

    const onFinish = async () => {
        //TODO вынести функционал подписывания в отдельный хук
        if (name !== '') {
            if (name.length > accNameMaxLength) {
                setResponseError(accNameMaxLengthErrMsg)

                return
            }

            const requestUuid = uuidv4()
            const thumbprint = await SignUtils.getCertificateThumbprint(
                data.certificate_data.certificate
            )
            const totalInfo = { name }
            const encodedInfo = encode(JSON.stringify(totalInfo))
            const encoded = requestUuid + encodedInfo
            const preSignature = await SignUtils.SignCreate(thumbprint, encoded)
            const signature = btoa(hexToBinary(preSignature))

            // Создание pdf и sig файлов Распоряжения об открытии лицевого счета
            const requestGetPDF = handleRequestGetPDF()
            const signedPdfCreated = await createSignedPdf(
                requestUuid,
                thumbprint,
                EServiceIDs.Users,
                requestGetPDF
            )
            if (signedPdfCreated.error) {
                console.log(signedPdfCreated.error)
                setResponseError('Ошибка создания файла "Распоряжение об открытии лицевого счета"')

                return
            }

            await openAccountService({
                userInfo: totalInfo,
                uuid: requestUuid,
                sign: signature,
                userId,
                onFinish: () => {
                    setIsModalVisible(false)
                    dispatch(getAccounts())
                },
                onError: (error) => {
                    setResponseError(errorHandler(error.err_code))
                },
            })
        } else {
            setResponseError('Введите наименование лицевого счета')
        }
    }

    // Собрать данные для формирования и подписания pdf
    const handleRequestGetPDF = () => {
        const requestGetPDF: IRequestGetPDF = {
            template_name:
                data?.user_type == EUserType.FizLico
                    ? 'open_account_number_natural_person'
                    : data?.user_type == EUserType.IP
                    ? 'open_account_number_individual_entrepreneur'
                    : data?.without_attorney
                    ? 'open_account_number_legal_entity_DG'
                    : 'open_account_number_legal_entity',
            data: {
                user_name: `${data?.last_name} ${data?.first_name} ${data?.middle_name}`.trim()
            },
        }
        switch (data?.user_type) {
            case EUserType.UrLico:
                requestGetPDF.data.company_name = data.company_name
                requestGetPDF.data.inn_kpp_ogrn =
                    data.company_inn + '/' + data.kpp + '/' + data.ogrn
                requestGetPDF.data.company_position = !!data.company_position ? data.company_position : '-'
                if (!data?.without_attorney) {
                    requestGetPDF.data.attorney_number = data.machine_readable_attorney?.number
                }
                break
            case EUserType.FizLico:
                requestGetPDF.data.snils = data.snils
                break
            case EUserType.IP:
                requestGetPDF.data.ogrnip = data.ogrn
                break
            default:
                break
        }

        return requestGetPDF
    }

    if (isLoading) {
        return <CenteredSpin />
    }

    return (
        <Modal isModalVisible={isModalVisible}>
            <div className={style.modalWrapper}>
                <div className={style.modalHeader}>{t('myProfile.openPersonalAccount')}</div>
                <Alert
                    message={'Номер счета присваивается автоматически после открытия'}
                    className={style.accountAlert}
                    type="info"
                    showIcon
                />
                <div className={style.modalText}>
                    <div>{t('myProfile.accountName')}</div>
                    <Input
                        className={style.input}
                        maxLength={accNameMaxLength}
                        value={name}
                        onChange={onChangeName}
                    />
                </div>
                <Alert
                    className={style.modalAlert}
                    message={
                        <div className={style.modalAlertText}>
                            <div>
                                За операцию взымается оплата в размере{' '}
                                <Typography.Text strong>{rate}</Typography.Text>
                            </div>
                            <div>Баланс платежного счета: {balance}</div>
                        </div>
                    }
                    type="warning"
                    showIcon
                />
                {responseError && (
                    <Alert
                        className={style.responseError}
                        message={responseError}
                        type="error"
                        showIcon
                    />
                )}
                {isSignExpired && (
                    <Alert
                        className={style.signError}
                        message={'Истек срок действия ЭП'}
                        type="error"
                        showIcon
                    />
                )}
                {(isEmptyAttorney || isAttorneyExpired) && (
                    <Alert
                        className={style.signError}
                        message={
                            isEmptyAttorney
                                ? t('profile.attorneyIsMissing')
                                : t('profile.attorneyIsExpired')
                        }
                        type="error"
                        showIcon
                    />
                )}
                <div className={style.buttonsBlock}>
                    <ButtonsBlock
                        confirmAction={onFinish}
                        cancelAction={handleCancel}
                        disableCondition={
                            !name ||
                            name?.length > accNameMaxLength ||
                            isSignExpired ||
                            isEmptyAttorney ||
                            isAttorneyExpired
                        }
                        confirmTitle={t('myProfile.openAccount')}
                    />
                </div>
            </div>
        </Modal>
    )
}

export default OpenAccountModal
